/**
 * documents.ts — the two derivations `get_agreement_documents` performs in SQL, restated for
 * the surfaces that cannot call it.
 *
 * TWO FACTS ABOUT A DOCUMENT STOPPED BEING COLUMNS: when it was completed, and which contract
 * it belongs to. `completed_at` is now derived from the signature rows, and `contract_id` is
 * NULL forever on a deposit agreement created before its deposit was attached — the owning
 * contract is resolved through `contract_deposit_agreements → contract_deposits` on every
 * read, with no backfill and therefore no second copy to disagree.
 *
 * Every consumer that goes through `get_agreement_documents` gets both for free. Two cannot:
 * the deposit sheet, whose deposits have no contract to key that function on, and the
 * cross-contract "awaiting finalization" list, which is keyed on no contract at all. So the
 * rules live here once — in a module with no Supabase client in its graph, which is what makes
 * them unit-testable — rather than twice inside two components.
 */

/** A PostgREST embed: an object for a to-one, an array for a to-many, absent for neither. */
export type Embedded<T> = T | T[] | null | undefined;

/**
 * The one row behind an embed, whichever way PostgREST answered it.
 *
 * Every embed this module reads is to-one by foreign key, but supabase-js types some of them
 * as arrays, and a reading that assumed either shape would silently produce `undefined` the
 * moment the other one arrived.
 */
export function firstEmbed<T>(value: Embedded<T>): T | null {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
}

/** The minimum of a signature row these rules read. */
export interface CountedSignature {
    status: string;
    signed_at: string | null;
}

/**
 * When every party had signed, or null while one still has not.
 *
 * VOIDED ROWS NEITHER COUNT NOR BLOCK. A signature that was voided out is not a party still
 * being waited on, so a document whose remaining seats are all SIGNED is complete — and the
 * completion time is the last of those signatures.
 *
 * IT IS NOT READ OFF THE DOCUMENT'S STATUS, deliberately. A document that says COMPLETED
 * while a signature has since been voided out from under it would otherwise report a
 * completion that no longer holds; the rows are the evidence and the status is a summary of
 * them.
 */
export function completionTime(signatures: CountedSignature[]): string | null {
    const counted = signatures.filter((signature) => signature.status !== 'VOIDED');
    if (counted.length === 0) return null;
    if (!counted.every((signature) => signature.status === 'SIGNED')) return null;
    return (
        counted
            .map((signature) => signature.signed_at)
            .filter((at): at is string => !!at)
            .sort()
            .pop() ?? null
    );
}

/** The deposit arc, embedded far enough to reach the contract the deposit was attached to. */
export interface DepositArcEmbed {
    contract_deposits: Embedded<{ contract_id: number | null }>;
}

/**
 * The contract a document belongs to, resolved the way the server resolves it.
 *
 * THE COLUMN ALONE IS NOT THE ANSWER, AND ON THE DEPOSIT ARC IT NEVER BECOMES ONE. A deposit
 * agreement signed before any contract existed carries `contract_id` NULL permanently, so a
 * surface that trusted the column would print "#null" for a deposit that has been part of a
 * contract for months — and link an admin to `/dashboard/contracts/null`. Returns null only
 * when the deposit genuinely belongs to nobody yet, which is the state this arc exists for.
 */
export function effectiveContractId(document: {
    contract_id: number | null;
    contract_deposit_agreements?: Embedded<DepositArcEmbed>;
}): number | null {
    if (document.contract_id != null) return document.contract_id;
    const arc = firstEmbed(document.contract_deposit_agreements);
    return firstEmbed(arc?.contract_deposits)?.contract_id ?? null;
}
